import $ from 'jquery';
import _ from 'underscore';
import Backbone from "backbone";
import Chaplin from "chaplin";
import UpgradeToTeamPopupView from "views/workspace/UpgradeToTeamPopupView";
import TrebbleClientAPIHelper from "models/helper/TrebbleClientAPI";
import LocalStorageHelper from "models/helper/LocalStorageHelper";
import { WorkspaceRole, WorkspaceStatus } from "models/helper/WorkspaceConstants";
import {
    TRANSITION_MODES,
    getWorkspaceTransitionStrategy,
    getStepsForTransition,
} from "models/helper/WorkspaceTransitionStrategy";
import ti18n from "i18n!nls/WorkspaceManagei18n";
import RSVP from "rsvp";

/**
 * UpgradeToTeamPopupController
 *
 * Drives the upgrade popup with one of two transition strategies:
 *
 *   - 'create'           (default) for users without a workspace yet.
 *   - 'upgrade_in_place' for Free users whose existing workspace is
 *                        being transformed in place.
 *
 * Strategy is selected via the `mode` option and defaults to CREATE for
 * backward compatibility with every existing call site.
 */
const UpgradeToTeamPopupController = Chaplin.Controller.extend({

    initialize: function(options) {
        _.extend(this, Backbone.Events);
        this._parentController = options.parentController;
        this._pendingWorkspaceId = null;
        this._pendingPlanType = null;
        // Strategy selection is baked in at construction — every call site
        // passes the mode explicitly (or omits it to get CREATE).
        this._mode = (options && options.mode) || TRANSITION_MODES.CREATE;
        this._strategy = getWorkspaceTransitionStrategy(this._mode);
    },

    getModel: function() {
        return this.model;
    },

    getView: function() {
        return this.view;
    },

    getMode: function() {
        return this._mode;
    },

    onPopupClosed: function() {
        // Cancel any pending workspace if the user closed the popup on the
        // payment step. The strategy decides whether to release an in-place
        // upgrade guard or cleanup a pending workspace row.
        // Guard: do NOT cancel if a finalize call is currently in flight —
        // finalize is the success path and must always win over a stray
        // popup-close cleanup.
        if (!this._finalizingUpgrade && this._pendingWorkspaceId) {
            const wsId = this._pendingWorkspaceId;
            this._pendingWorkspaceId = null;
            this._pendingPlanType = null;
            this._strategy.handleCancelUpgrade(wsId)
                .catch(function(e) { console.warn('Failed to cancel pending workspace on popup close:', e); });
        }
    },

    handleStartUpgrade: async function(data) {
        const result = await this._strategy.handleStartUpgrade({
            workspaceName: data.workspaceName,
            planType: data.planType,
            billingCycle: data.billingCycle,
        });
        this._pendingWorkspaceId = result.workspaceId;
        this._pendingPlanType = data.planType || null;
        return { clientSecret: result.clientSecret, workspaceId: result.workspaceId };
    },

    handleMountStripeElement: function(container, subscriptionInfo) {
        return this._strategy.handleMountStripeElement(container, subscriptionInfo);
    },

    handleCompletePayment: async function(stripeElementsInfo) {
        // Pass the pending workspaceId so the Stripe layer can dedup
        // concurrent confirmPayment calls (double-clicked Subscribe, etc.).
        await this._strategy.handleCompletePayment(stripeElementsInfo, this._pendingWorkspaceId);
    },

    handleFinalizeUpgrade: async function(workspaceId) {
        this._finalizingUpgrade = true;
        try {
            return await this._handleFinalizeUpgradeInternal(workspaceId);
        } finally {
            this._finalizingUpgrade = false;
        }
    },

    _handleFinalizeUpgradeInternal: async function(workspaceId) {
        const localStorageHelper = LocalStorageHelper.getInstance();
        const result = await this._strategy.handleFinalizeUpgrade(workspaceId);
        const currentWorkspaceContext = localStorageHelper.getWorkspaceContext();
        let storedWorkspaceList = [];
        try {
            storedWorkspaceList = localStorageHelper.getWorkspaceList() || [];
        } catch (error) {
            storedWorkspaceList = [];
        }
        const matchingStoredWorkspace = storedWorkspaceList.find(function(workspace) {
            return workspace && workspace.workspaceId === workspaceId;
        });
        const workspaceSummary = result && result.workspaceSummary ? result.workspaceSummary : {};
        const workspaceContext = Object.assign({}, workspaceSummary, {
            workspaceId: (result && result.workspaceId) || workspaceSummary.workspaceId || workspaceId,
            workspaceName: (result && result.workspaceName)
                || workspaceSummary.workspaceName
                || (
                    currentWorkspaceContext
                    && currentWorkspaceContext.workspaceId === workspaceId
                    && currentWorkspaceContext.workspaceName
                )
                || (matchingStoredWorkspace && matchingStoredWorkspace.workspaceName)
                || '',
            myRole: (result && result.myRole) || workspaceSummary.myRole || workspaceSummary.role || WorkspaceRole.OWNER,
            isAdmin: (result && typeof result.isAdmin === 'boolean') ? result.isAdmin : (
                typeof workspaceSummary.isAdmin === 'boolean' ? workspaceSummary.isAdmin : true
            ),
            workspaceStatus: (result && result.workspaceStatus) || workspaceSummary.workspaceStatus || WorkspaceStatus.ACTIVE,
            planType: (result && result.planType) || workspaceSummary.planType || this._pendingPlanType,
            memberRecordId: (result && result.memberRecordId) || workspaceSummary.memberRecordId || null,
            minutesUsedThisMonth: result && result.minutesUsedThisMonth != null
                ? result.minutesUsedThisMonth
                : workspaceSummary.minutesUsedThisMonth,
            minutesTotal: result && result.minutesTotal != null
                ? result.minutesTotal
                : workspaceSummary.minutesTotal,
            logoUrl: (result && result.logoUrl) || workspaceSummary.logoUrl || null,
        });
        let workspaceFound = false;
        const nextWorkspaceList = (storedWorkspaceList || []).map(function(workspace) {
            if (workspace && workspace.workspaceId === workspaceContext.workspaceId) {
                workspaceFound = true;
                return Object.assign({}, workspace, workspaceContext);
            }
            return workspace;
        });
        if (!workspaceFound) {
            nextWorkspaceList.push(workspaceContext);
        }
        try {
            localStorageHelper.setWorkspaceList(nextWorkspaceList);
        } catch (storageError) {
            console.warn('Failed to persist workspace list after workspace upgrade:', storageError);
        }
        try {
            localStorageHelper.setWorkspaceContext(workspaceContext);
        } catch (storageError) {
            console.warn('Failed to persist workspace context after workspace upgrade:', storageError);
        }
        this._notifyWorkspaceLibraryUpdated(workspaceContext);
        this._refreshWorkspaceListAfterFinalize(workspaceContext);
        this._pendingWorkspaceId = null;
        this._pendingPlanType = null;
        window.alertSuccessMessage(window.getI18n(ti18n, "WORKSPACE_CREATED"));
    },

    handleInviteMembers: async function(emails, workspaceIdOverride) {
        if (!Array.isArray(emails) || emails.length === 0) {
            return;
        }
        const workspaceId = workspaceIdOverride || this._pendingWorkspaceId;
        if (!workspaceId) {
            throw new Error('No pending workspaceId for invite');
        }
        const apiHelper = TrebbleClientAPIHelper.getInstance();
        for (const email of emails) {
            await apiHelper.inviteWorkspaceMember({
                workspaceId: workspaceId,
                email: email,
            });
        }
    },

    handleCancelUpgrade: async function(workspaceId) {
        this._pendingWorkspaceId = null;
        this._pendingPlanType = null;
        try {
            await this._strategy.handleCancelUpgrade(workspaceId);
        } catch (e) {
            console.warn('Failed to cancel workspace upgrade:', e);
        }
    },

    _refreshWorkspaceListAfterFinalize: function(preservedWorkspaceContext) {
        TrebbleClientAPIHelper.getInstance().getMyWorkspaces().then(function(workspaces) {
            var localStorageHelper = LocalStorageHelper.getInstance();
            localStorageHelper.setWorkspaceList(
                localStorageHelper.mergeWorkspaceListWithContext(workspaces || [], preservedWorkspaceContext)
            );
        }).catch(function(error) {
            console.warn('Failed to refresh workspace list after workspace upgrade:', error);
        });
    },

    _notifyWorkspaceLibraryUpdated: function(preservedWorkspaceContext) {
        if (typeof window !== "undefined" && window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent("trebble:workspace-library-updated", {
                detail: {
                    preservedWorkspaceContext: preservedWorkspaceContext || null
                }
            }));
        }
    },

    buildModelAndRenderView: function() {
        return new RSVP.Promise((function(resolve, reject) {
            try {
                const localStorageHelper = LocalStorageHelper.getInstance();
                const userInfo = localStorageHelper.getUserInfo();
                const userName = userInfo ? (userInfo.fullName || userInfo.username || '') : '';
                const nameTemplate = window.getI18n(ti18n, "WORKSPACE_DEFAULT_NAME_TEMPLATE") || "{0}'s Team";
                const currentWorkspaceContext = localStorageHelper.getWorkspaceContext();
                const inPlaceWorkspaceName = this._mode === TRANSITION_MODES.UPGRADE_IN_PLACE
                    && currentWorkspaceContext
                    && currentWorkspaceContext.workspaceName
                    ? currentWorkspaceContext.workspaceName
                    : '';
                const defaultName = inPlaceWorkspaceName || (userName ? nameTemplate.replace('{0}', userName) : '');

                const params = {
                    mode: this._mode,
                    onStartUpgrade: this.handleStartUpgrade.bind(this),
                    onMountStripeElement: this.handleMountStripeElement.bind(this),
                    onCompletePayment: this.handleCompletePayment.bind(this),
                    onFinalizeUpgrade: this.handleFinalizeUpgrade.bind(this),
                    onCancelUpgrade: this.handleCancelUpgrade.bind(this),
                    onInviteMembers: this.handleInviteMembers.bind(this),
                    i18nModule: ti18n,
                    defaultName: defaultName,
                    // Step visibility is driven by planType (extra_editors step
                    // is Team-only) and mode (upgrade_in_place skips name).
                    // Computed inside the view for each plan-type change.
                    getStepsForTransition: getStepsForTransition,
                };

                this.view = new UpgradeToTeamPopupView(params);
                this.listenTo(this.view, "closed", this.onPopupClosed);

                this.view.render();

                resolve(this);
            } catch (error) {
                reject(error);
            }
        }).bind(this));
    }
});

export default UpgradeToTeamPopupController;
